/**
 * Intent-aware recommendation tracks for /intake/results.
 *
 * The qualification engine (backend) already computes per-product status:
 *   perProduct = { tirzepatide: {status:'APPROVED'|'FLAG'|'BLOCK', reasons:[]}, nad:{}, sermorelin:{} }
 *
 * This module turns that into a render plan based on the user's INTENT:
 *
 *   TRACK A — kind:'program' → bundles only, fall back to singles if no bundle qualifies
 *   TRACK B — kind:'product' → singles only, never mix bundles
 *   TRACK C — kind:'generic' → two sections (bundles, then singles)
 *
 * A bundle qualifies ONLY if every one of its components is non-BLOCK
 * (APPROVED or FLAG is fine — both pass).
 */

// Bundles ranked from highest-tier (most components / max doses) → lowest-tier.
// When a picked bundle doesn't qualify, we walk DOWN this list for the next-best.
export const BUNDLE_ORDER = [
  "advanced-transformation",   // 3 components, max doses
  "total-body-optimization",   // 3 components, standard doses
  "metabolic-boost",           // 2 components (no Sermorelin)
  "tirzepatide-core",          // 1 component
];

export const BUNDLE_COMPONENTS = {
  "tirzepatide-core":         ["tirzepatide"],
  "metabolic-boost":          ["tirzepatide", "nad"],
  "total-body-optimization":  ["tirzepatide", "nad", "sermorelin"],
  "advanced-transformation":  ["tirzepatide", "nad", "sermorelin"],
};

// Single-product priority order (Tirzepatide is the primary offering).
export const PRODUCT_PRIORITY = ["tirzepatide", "nad", "sermorelin"];

const NOTE_ADJUSTED = "Based on your answers, we've adjusted your recommendation to a program that's safer for you.";
const NOTE_FALLBACK = "We couldn't find a matching program — here's what you can purchase individually:";
const NOTE_SAFER    = "Based on your answers, we've recommended safer options for you.";

const notBlocked = (perProduct, slug) =>
  !!perProduct[slug] && perProduct[slug].status !== "BLOCK";

export function bundleQualifies(slug, perProduct) {
  const components = BUNDLE_COMPONENTS[slug] || [];
  if (components.length === 0) return false;
  return components.every(c => notBlocked(perProduct, c));
}

export function qualifyingBundles(perProduct) {
  return BUNDLE_ORDER.filter(slug => bundleQualifies(slug, perProduct));
}

export function qualifyingSingles(perProduct) {
  return PRODUCT_PRIORITY.filter(slug => notBlocked(perProduct, slug));
}

/** Qualifying bundles BELOW the picked one in BUNDLE_ORDER (most-similar → least). */
export function bundlesBelow(pickedSlug, perProduct) {
  const idx = BUNDLE_ORDER.indexOf(pickedSlug);
  if (idx === -1) return qualifyingBundles(perProduct);
  return BUNDLE_ORDER.slice(idx + 1).filter(slug => bundleQualifies(slug, perProduct));
}

/**
 * Main entry point. Returns a "render plan" object whose shape depends on `mode`:
 *
 *   { mode: 'global_block' }
 *   { mode: 'bundles',           bundles:[slug…], primary:slug, note:string|null }
 *   { mode: 'singles_only',      singles:[slug…], primary:slug, note:string|null }
 *   { mode: 'singles_fallback',  singles:[slug…], primary:slug, note:string }
 *   { mode: 'generic',           bundles:[…], singles:[…], primaryBundle, primarySingle }
 */
export function getRecommendations(intent, perProduct, globalBlock = false) {
  intent = intent || { kind: "generic" };
  if (globalBlock) return { mode: "global_block" };

  // ── TRACK A — bundle intent ────────────────────────────────────────────────
  if (intent.kind === "program" && intent.slug) {
    const picked = intent.slug;
    const allQualifying = qualifyingBundles(perProduct);

    if (bundleQualifies(picked, perProduct)) {
      // Picked first, then ALL other qualifying bundles in priority order.
      const others = allQualifying.filter(slug => slug !== picked);
      return {
        mode: "bundles",
        bundles: [picked, ...others],
        primary: picked,
        note: null,
      };
    }
    if (allQualifying.length > 0) {
      return {
        mode: "bundles",
        bundles: allQualifying,
        primary: allQualifying[0],
        note: NOTE_ADJUSTED,
      };
    }
    // Fall through to single-product fallback
    const singles = qualifyingSingles(perProduct);
    if (singles.length === 0) return { mode: "global_block" };
    return {
      mode: "singles_fallback",
      singles,
      primary: singles[0],
      note: NOTE_FALLBACK,
    };
  }

  // ── TRACK B — single product intent ────────────────────────────────────────
  if (intent.kind === "product" && intent.slug) {
    const picked = intent.slug;
    const nadQualifies = notBlocked(perProduct, "nad");

    // NAD+ always shows by itself if picked
    if (picked === "nad") {
      return {
        mode: "singles_only",
        singles: ["nad"],
        primary: "nad",
        note: null,
      };
    }

    // Sermorelin selected
    if (picked === "sermorelin") {
      if (notBlocked(perProduct, "sermorelin")) {
        // Sermorelin qualifies → show sermorelin + NAD+ (if NAD qualifies)
        const singles = nadQualifies ? ["sermorelin", "nad"] : ["sermorelin"];
        return {
          mode: "singles_only",
          singles,
          primary: "sermorelin",
          note: null,
        };
      }
      // Sermorelin blocked → show only NAD+ as fallback
      if (nadQualifies) {
        return {
          mode: "singles_only",
          singles: ["nad"],
          primary: "nad",
          note: NOTE_SAFER,
        };
      }
      return { mode: "global_block" };
    }

    // Tirzepatide selected
    if (picked === "tirzepatide") {
      if (notBlocked(perProduct, "tirzepatide")) {
        // Tirzepatide qualifies → show tirzepatide + NAD+ (if NAD qualifies)
        const singles = nadQualifies ? ["tirzepatide", "nad"] : ["tirzepatide"];
        return {
          mode: "singles_only",
          singles,
          primary: "tirzepatide",
          note: null,
        };
      }
      // Tirzepatide blocked → show only NAD+ as fallback
      if (nadQualifies) {
        return {
          mode: "singles_only",
          singles: ["nad"],
          primary: "nad",
          note: NOTE_SAFER,
        };
      }
      return { mode: "global_block" };
    }

    // Unknown product slug → fallback to NAD+ if available
    if (nadQualifies) {
      return {
        mode: "singles_only",
        singles: ["nad"],
        primary: "nad",
        note: NOTE_SAFER,
      };
    }
    return { mode: "global_block" };
  }

  // ── TRACK C — generic intent ───────────────────────────────────────────────
  const bundles = qualifyingBundles(perProduct);
  const singles = qualifyingSingles(perProduct);
  if (bundles.length === 0 && singles.length === 0) return { mode: "global_block" };
  return {
    mode: "generic",
    bundles,
    singles,
    primaryBundle: bundles[0] || null,
    primarySingle: singles[0] || null,
    note: null,
  };
}
